import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { MsalProvider } from '@azure/msal-react'
import { EventType } from '@azure/msal-browser'
import type { AuthenticationResult, EventMessage } from '@azure/msal-browser'
import { msalInstance } from './msalConfig'
import { AppShell } from './components/AppShell'

/**
 * MSAL v3+ requires `initialize()` to resolve before any other
 * PublicClientApplication call, and the redirect response (if the browser
 * just came back from Entra) has to be handled once, up front, before
 * `MsalProvider` mounts anything that reads auth state.
 */
async function bootstrap() {
  await msalInstance.initialize()

  try {
    await msalInstance.handleRedirectPromise()
  } catch (error) {
    // Surfaced to the sign-in screen implicitly: the operator just sees
    // "not authenticated" and can retry. Logged here so it's visible in
    // the browser console rather than silently swallowed.
    console.error('CHU-GPT Admin: redirect handling failed', error)
  }

  const accounts = msalInstance.getAllAccounts()
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0] ?? null)
  }

  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const result = event.payload as AuthenticationResult
      if (result.account) {
        msalInstance.setActiveAccount(result.account)
      }
    }
  })

  const container = document.getElementById('root')
  if (!container) {
    throw new Error('CHU-GPT Admin: #root element missing from index.html')
  }

  createRoot(container).render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </MsalProvider>
    </StrictMode>,
  )
}

void bootstrap()
